import React, { createContext, useContext, useState, useEffect } from 'react';
import { settingsAPI, productsAPI, reviewsAPI, navigationAPI, contentAPI, initAPI } from '../lib/api';

const SiteContext = createContext(null);

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within SiteProvider');
  }
  return context;
};

export const SiteProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [headerLinks, setHeaderLinks] = useState([]);
  const [footerLinks, setFooterLinks] = useState([]);
  const [contentSections, setContentSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Initialize data first
      try {
        await initAPI.init();
      } catch (e) {
        // Already initialized, ignore
      }

      const [settingsRes, productsRes, reviewsRes, navRes, contentRes] = await Promise.all([
        settingsAPI.get(),
        productsAPI.getAll(),
        reviewsAPI.getAll(),
        navigationAPI.getAll(),
        contentAPI.getAll(),
      ]);

      setSettings(settingsRes.data);
      setProducts(productsRes.data);
      setReviews(reviewsRes.data);
      
      const navLinks = navRes.data;
      setHeaderLinks(navLinks.filter(l => l.location === 'header'));
      setFooterLinks(navLinks.filter(l => l.location === 'footer'));
      
      const sections = {};
      contentRes.data.forEach(s => {
        sections[s.section_key] = s;
      });
      setContentSections(sections);
      
      setError(null);
    } catch (e) {
      console.error('Failed to fetch site data:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const refreshData = () => fetchData();

  const value = {
    settings,
    products,
    reviews,
    headerLinks,
    footerLinks,
    contentSections,
    loading,
    error,
    refreshData,
  };

  return (
    <SiteContext.Provider value={value}>
      {children}
    </SiteContext.Provider>
  );
};

export default SiteContext;
